
import { defineComponent, ref, computed, onMounted, watch, reactive } from 'vue';
import { useStore } from 'vuex';
import { POST_DONASI, PUT_DONASI } from '@/store/donasi.module';
import { POST_IMAGE } from '@/store/upload.module';
import { GET_FAKULTAS } from '@/store/fakultas.module';
import type { Fakultas } from '@/store/fakultas.module';
import { showError } from '@/utils/swal';
import AppSelect from '@/components/input/AppSelect.vue';
import type { ApiErrorResponse, Donation, DonationPayload } from '@/types/domain';

const DONATION_TYPE_OPTIONS = [
  'iuran_sukarela',
  'kontribusi_anggota',
  'kontribusi_donatur',
  'pembelian_merchandise',
  'kontribusi_sukarela',
];

const formatDonationType = (value: string) =>
  value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

const toInputDate = (value: unknown) => {
  if (!value) return '';
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return String(value);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export default defineComponent({
  components: {
    AppSelect,
  },
  props: {
    title: { type: String, required: true },
    id: { type: String, required: false },
    data: { type: Object, required: false, default: () => ({}) },
  },
  setup(props, { emit }) {
    const store = useStore();
    const initialData = props.data as Partial<Donation>;
    const modalContent = ref(null);
    const isLoading = ref(false);
    const facultyId = ref<number | null>(initialData.facultyId ?? initialData.faculty?.id ?? null);
    const imageMode = ref(initialData.proof ? 'url' : 'upload');
    const imageFile = ref<File | null>(null);
    const imagePreviewLocal = ref('');
    const imageUrlInput = ref(typeof initialData.proof === 'string' ? initialData.proof : '');

    const donationTypeOptions = ref(
      DONATION_TYPE_OPTIONS.map((value) => ({
        value,
        label: formatDonationType(value),
      }))
    );
    const donationTypeSelectOptions = computed(() => [
      { value: '', label: 'Pilih jenis donasi' },
      ...donationTypeOptions.value,
    ]);
    const notificationOptions = [
      { value: 'whatsapp', label: 'WhatsApp' },
      { value: 'email', label: 'Email' },
    ];

    const isEditMode = Boolean(props.id);
    const paymentMethodOptions = [
      { value: 'manual', label: 'Manual' },
      { value: 'midtrans', label: 'Midtrans' },
    ];
    const paymentStatusOptions = [
      { value: 'pending', label: 'Menunggu' },
      { value: 'settlement', label: 'Lunas' },
      { value: 'expired', label: 'Kedaluwarsa' },
      { value: 'failed', label: 'Gagal' },
      { value: 'refunded', label: 'Dikembalikan' },
    ];

    const formData = reactive({
      id: '',
      data: {
        ...initialData,
        name: initialData.name || '',
        email: initialData.email || '',
        noWhatsapp: initialData.noWhatsapp || '',
        donationType: initialData.donationType || '',
        amount: initialData.amount ?? initialData.grossAmount ?? '',
        date: toInputDate(initialData.date),
        bank: initialData.bank || '',
        notification: Array.isArray(initialData.notification) ? initialData.notification : [],
        nameIsHidden: initialData.options?.nameIsHidden || initialData.nameIsHidden || false,
        isHambaAllah: initialData.options?.isHambaAllah || initialData.isHambaAllah || false,
        image: initialData.proof || '',
        paymentMethod: initialData.paymentMethod || 'manual',
        paymentStatus: initialData.paymentStatus || (isEditMode ? initialData.paymentStatus : 'settlement'),
      } as DonationPayload,
    });

    const fakultasList = computed<Fakultas[]>(() => store.getters.fakultas || []);
    const facultyOptions = computed(() => [
      { value: null, label: 'Tidak terkait fakultas' },
      ...fakultasList.value.map((f) => ({
        value: f.id,
        label: `${f.name} (kode unik: ${f.kodeUnik})`,
      })),
    ]);
    const selectedFaculty = computed<Fakultas | undefined>(() =>
      fakultasList.value.find((f) => f.id === facultyId.value)
    );

    const formattedAmount = computed(() => {
      const amount = Number(formData.data.amount || 0);
      if (!amount || Number.isNaN(amount)) return '';
      return new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
        minimumFractionDigits: 0,
      }).format(amount);
    });

    const notificationValue = computed(() =>
      Array.isArray(formData.data.notification) ? formData.data.notification.join(',') : ''
    );

    const imagePreview = computed(() => {
      if (imagePreviewLocal.value) return imagePreviewLocal.value;
      return imageUrlInput.value || '';
    });

    const closeModal = () => {
      isLoading.value = false;
      emit('close');
    };

    const onAmountInput = (event: Event) => {
      const input = event.target as HTMLInputElement;
      const rawValue = input.value.replace(/\D/g, '');
      formData.data.amount = rawValue ? Number(rawValue) : '';
      input.value = formattedAmount.value;
    };

    const setImageMode = (mode: 'url' | 'upload') => {
      imageMode.value = mode;
      if (mode === 'url') {
        imageFile.value = null;
        imagePreviewLocal.value = '';
        formData.data.image = imageUrlInput.value;
      }
    };

    const onImageUrlInput = () => {
      imageFile.value = null;
      imagePreviewLocal.value = '';
      formData.data.image = imageUrlInput.value;
    };

    const onImageChange = (event: Event) => {
      const input = event.target as HTMLInputElement;
      const file = input.files?.[0] || null;
      imageFile.value = file;
      if (!file) {
        imagePreviewLocal.value = '';
        formData.data.image = '';
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        imagePreviewLocal.value = (reader.result as string) || '';
      };
      reader.readAsDataURL(file);
      formData.data.image = file;
    };

    const removeImage = () => {
      imageFile.value = null;
      imagePreviewLocal.value = '';
      imageUrlInput.value = '';
      formData.data.image = '';
    };

    const handleSubmit = async () => {
      isLoading.value = true;
      try {
        const payload: DonationPayload = {
          ...formData.data,
          facultyId: facultyId.value,
        };

        if (payload.image) {
          if (typeof payload.image !== 'string') {
            const response = await store.dispatch(POST_IMAGE, { data: payload.image });
            payload.proof = response || '';
          } else {
            payload.proof = payload.image;
          }
          delete payload.image;
        }

        if (props.id) {
          formData.id = props.id;
          await store.dispatch(PUT_DONASI, { id: formData.id, data: payload });
        } else {
          await store.dispatch(POST_DONASI, { data: payload });
        }
        closeModal();
      } catch (error: unknown) {
        const apiError = error as ApiErrorResponse;
        showError('Gagal', apiError?.response?.data?.message || apiError?.message || 'Gagal menyimpan donasi');
        isLoading.value = false;
      }
    };

    watch(
      () => props.data?.facultyId,
      (v) => {
        if (v != null) facultyId.value = v;
      }
    );

    onMounted(async () => {
      try {
        if (!fakultasList.value.length) await store.dispatch(GET_FAKULTAS);
      } catch (err) {
        showError('Gagal', err instanceof Error ? err.message : 'Gagal memuat daftar fakultas');
      }
    });

return {
      modalContent,
      closeModal,
      handleSubmit,
      isLoading,
      donationTypeOptions,
      donationTypeSelectOptions,
      notificationOptions,
      fakultasList,
      facultyOptions,
      facultyId,
      selectedFaculty,
      formData,
      formattedAmount,
      notificationValue,
      imageMode,
      imageUrlInput,
      imagePreview,
      setImageMode,
      onAmountInput,
      onImageUrlInput,
      onImageChange,
      removeImage,
      paymentMethodOptions,
      paymentStatusOptions,
    };
  },
});
